import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Loader2, Mail, Trash2 } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import { useConnectGmail, useDisconnectGmail, useUserGmail } from '../../hooks/useUserGmail';
import type { GmailConnection } from '../../lib/userGmailApi';

/**
 * Per-user Gmail integration.
 *
 * Lists a user's connected Gmail accounts with status, and lets the user
 * connect a new one (OAuth) or disconnect. An ADMIN viewing another user sees
 * the same status list (read-only) but cannot connect on their behalf — the
 * OAuth grant must come from that user's own Google login.
 */
export function GmailIntegration({ userId }: { userId: string }) {
  const currentUser = useAuthStore((s) => s.user);
  const isSelf = currentUser?.id === userId;
  const isAdmin = currentUser?.role === 'ADMIN';

  const { data, isLoading, isError } = useUserGmail(userId);
  const connect = useConnectGmail(userId);
  const disconnect = useDisconnectGmail(userId);

  const [searchParams] = useSearchParams();
  const gmailResult = searchParams.get('gmail');
  const gmailReason = searchParams.get('reason');

  const [label, setLabel] = useState('');

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Se incarca conexiunile Gmail…
      </p>
    );
  }
  if (isError || !data) {
    return <p className="text-sm text-red-600">Conexiunile Gmail nu au putut fi incarcate.</p>;
  }

  const { connections, googleConfigured } = data;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Contul Gmail conectat aici este cel pe care Firmio AI îl citește pentru tine (inbox,
        căutări, rezumatul zilnic) și în care poate lăsa ciorne de răspuns. Nu trimite niciodată
        nimic: ciorna rămâne în Gmail › Drafts până o trimiți tu.
      </p>
      {/* OAuth return banner */}
      {gmailResult === 'connected' && (
        <p className="flex items-center gap-1.5 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          <CheckCircle2 className="h-4 w-4" /> Gmail conectat cu succes.
        </p>
      )}
      {gmailResult === 'error' && (
        <p className="flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4" /> Gmail nu a putut fi conectat
          {gmailReason ? ` (${gmailReason})` : ''}.
        </p>
      )}

      {!googleConfigured && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          Google OAuth nu este configurat. Un administrator trebuie să seteze Client ID și Client
          Secret în Setări → Integrări → Google Calendar; apoi fiecare membru își poate conecta
          propriul Gmail de aici.
        </p>
      )}

      {connections.length === 0 ? (
        <p className="text-sm text-gray-500">Niciun cont Gmail conectat.</p>
      ) : (
        <ul className="space-y-2">
          {connections.map((conn) => (
            <GmailConnectionRow
              key={conn.id}
              conn={conn}
              canManage={isSelf || isAdmin}
              canReauth={isSelf}
              onDisconnect={() => disconnect.mutate(conn.id)}
              onReauth={() => connect.mutate(conn.gmailLabel ?? undefined)}
              disconnecting={disconnect.isPending && disconnect.variables === conn.id}
            />
          ))}
        </ul>
      )}

      {/* Connect new Gmail — only the user themselves can grant access. */}
      {isSelf && googleConfigured && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Etichetă (ex. Serviciu, Personal)"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <button
            type="button"
            onClick={() => connect.mutate(label.trim() || undefined)}
            disabled={connect.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-40"
          >
            {connect.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
            Conecteaza Gmail
          </button>
        </div>
      )}
      {!isSelf && isAdmin && (
        <p className="text-xs text-gray-400">
          Vizualizare ca administrator: statusul conexiunii este doar pentru citire. Doar
          utilizatorul isi poate conecta propriul Gmail.
        </p>
      )}
    </div>
  );
}

function GmailConnectionRow({
  conn,
  canManage,
  canReauth,
  onDisconnect,
  onReauth,
  disconnecting,
}: {
  conn: GmailConnection;
  canManage: boolean;
  canReauth: boolean;
  onDisconnect: () => void;
  onReauth: () => void;
  disconnecting: boolean;
}) {
  const expired = conn.status === 'expired';
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-200 px-3 py-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-gray-900">{conn.gmailEmail}</span>
          {conn.gmailLabel && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
              {conn.gmailLabel}
            </span>
          )}
          {expired ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
              <AlertTriangle className="h-3 w-3" /> Expirat
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
              <CheckCircle2 className="h-3 w-3" /> Conectat
            </span>
          )}
          {!expired && !conn.canDraft && (
            <span
              className="rounded-full border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs text-gray-600"
              title="Conexiunea a fost făcută doar cu citire. Reautorizează ca Firmio AI să poată lăsa ciorne."
            >
              doar citire
            </span>
          )}
        </div>
        {conn.lastSyncAt && (
          <p className="mt-0.5 text-xs text-gray-400">
            Ultima sincronizare: {new Date(conn.lastSyncAt).toLocaleString('ro-RO')}
          </p>
        )}
      </div>

      {canManage && (
        <div className="flex items-center gap-1.5">
          {(expired || !conn.canDraft) && canReauth && (
            <button
              type="button"
              onClick={onReauth}
              className="rounded-md border border-amber-300 px-2.5 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50"
            >
              {expired ? 'Reautentifică' : 'Reautorizează pentru ciorne'}
            </button>
          )}
          <button
            type="button"
            onClick={onDisconnect}
            disabled={disconnecting}
            title="Deconecteaza"
            className="inline-flex items-center gap-1 rounded-md border border-red-300 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
          >
            {disconnecting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            Deconecteaza
          </button>
        </div>
      )}
    </li>
  );
}
